import { useMutation, useQueryClient } from '@tanstack/react-query';
import { handleApiResponse, notify } from '@/functions';
import { apiClient } from '@/components/api';
import routes from '@/lib/routes';

type TDataVariables = { _id: string };

export const useRequestDeleteAccountingUser = () => {

    const queryClient = useQueryClient();
    const { isPending, error, mutateAsync } = useMutation({
        mutationKey: [routes.api.accountingUsers, 'delete'],
        mutationFn: ({ _id }: TDataVariables) =>
            apiClient
                .delete(`${routes.api.accountingUsers}/${_id}`)
                .then(handleApiResponse),
        // Optimistic update: remove item BEFORE request completes
        onMutate: async ({ _id }) => {
            // Cancel any outgoing refetches
            await queryClient.cancelQueries({ queryKey: [routes.api.accountingUsers] });

            // Snapshot previous value for potential rollback
            const previousData = queryClient.getQueriesData({ queryKey: [routes.api.accountingUsers] });

            // Optimistically update cache - remove item immediately
            queryClient.setQueriesData(
                { queryKey: [routes.api.accountingUsers] },
                (oldData: any) => {
                    if (!oldData?.data) return oldData;
                    return {
                        ...oldData,
                        data: oldData.data.filter((user: any) => user._id !== _id)
                    };
                }
            );

            return { previousData };
        },
        onSuccess: () => {
            notify('L\'utilisateur comptable a été supprimé avec succès');
        },
        onError: (_error, _variables, context) => {
            // Rollback on error - restore previous data
            if (context?.previousData) {
                context.previousData.forEach(([queryKey, data]) => {
                    queryClient.setQueryData(queryKey, data);
                });
            }
            notify('Erreur lors de la suppression de l\'utilisateur', 'error');
            // Reload page to ensure consistent state
            window.location.reload();
        }
    });

    const trigger = (data: TDataVariables) =>
        mutateAsync(data);

    return ({
        isLoading: isPending,
        trigger,
        error
    });

};
